import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/UserContext';
import { CreditCard } from 'lucide-react';
import { 
    IconBurguerMenu, 
    IconCloseBurguerMenu, 
    IconEasyJurLegalOps,
    IconListDemand,
    IconOperator,
    IconProfessional,
    IconButtonDots,
    IconUser,
    IconBell,
    IconProfile
} from '../icons';
import ProfileMenu from '../Ui/ProfileMenu';
import { 
    Container, 
    MenuButton, 
    MenuContent, 
    MenuHeader,
    MenuList,
    MenuItem,
    LogoContainer,
    SectionFooter,
    ProfileFooter,
    DescriptionProfileFooter,
    Overlay
} from './styles';
import { comum } from '../../styles/Themes/light';

interface MobileMenuProps {
    counters: any;
}

export default function MobileMenu({ counters }: MobileMenuProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [openMenu, setOpenMenu] = useState(false);
    const [profile, setProfile] = useState<boolean>(false);
    const location = useLocation();
    const { user, pageName } = useAuth();
    const openRef = useRef<any>();
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (user?.profile === 'comum') {
            const userWithLetter = `D${user.id}`       
            setProfile(userWithLetter === pageName.split(' ')[1]);
        }

        if (user?.profile === 'operador') {
            const userWithLetter = `P${user.id}`
            setProfile(userWithLetter === pageName.split(' ')[1]);
        }

        if (user?.profile === 'admin') {
            const userWithLetter = `J${user.id}`
            setProfile(userWithLetter === pageName.split(' ')[1]);
        }
    }, [pageName, user]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (isOpen && menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const adminData = [
        {
            id: 3,
            name: 'Demandas',
            to: '/demandas',
            notification: counters?.qtd_demandas ? counters.qtd_demandas : 0,
            icon: <IconListDemand color={comum.colors.interfaceGray400}/>,
        },
        {
            id: 4,
            name: 'Profissionais',
            to: '/profissionais',
            notification: counters?.qtd_profissionais ? counters.qtd_profissionais : 0,
            icon: <IconProfessional color={comum.colors.interfaceGray400}/>,
        },
        {
            id: 5,
            name: 'Operadores',
            to: '/operadores',
            notification: counters?.qtd_operadores ? counters.qtd_operadores : 0,
            icon: <IconOperator color={comum.colors.interfaceGray400}/>,
        },
        {
            id: 7,
            icon: <IconBell color='#54666F'/>,
            name: 'Notificações',
            to: '/notificacoes',
            notification: counters?.qtd_notificacoes ? counters.qtd_notificacoes : 0,
            subMenu: []
        },
        {
            id: 8,
            icon: <IconProfile color='#54666F'/>,
            name: 'Solicitantes',
            to: '/solicitantes',
            notification: counters?.qtd_solicitantes ? counters.qtd_solicitantes : 0,
            subMenu: []
        },
        {
            id: 9,
            icon: <CreditCard size={22} strokeWidth={2} style={{ color: 'var(--c54666f)' }} />,
            name: 'Planos',
            to: '/planos',
            notification: counters?.qtd_planos ? counters.qtd_planos : 0,
            subMenu: []
        },
    ];

    const operatorData = [
        {
            id: 3,
            name: 'Demandas',
            to: '/demandas',
            notification: counters?.qtd_demandas ? counters.qtd_demandas : 0,
            icon: <IconListDemand color={comum.colors.interfaceGray400}/>,
        },
        {
            id: 4,
            name: 'Profissionais',
            to: '/profissionais',
            notification: counters?.qtd_profissionais ? counters.qtd_profissionais : 0,
            icon: <IconProfessional color={comum.colors.interfaceGray400}/>,
        },
        {
            id: 7,
            icon: <IconBell color='#54666F'/>,
            name: 'Notificações',
            to: '/notificacoes',
            notification: counters?.qtd_notificacoes ? counters.qtd_notificacoes : 0,
            subMenu: []
        },
        {
            id: 8,
            icon: <IconProfile color='#54666F'/>,
            name: 'Solicitantes',
            to: '/solicitantes',
            notification: counters?.qtd_solicitantes ? counters.qtd_solicitantes : 0,
            subMenu: []
        },
        {
            id: 9,
            icon: <CreditCard size={22} strokeWidth={2} style={{ color: 'var(--c54666f)' }} />,
            name: 'Planos',
            to: '/planos',
            notification: counters?.qtd_planos ? counters.qtd_planos : 0,
            subMenu: []
        },
    ];

    const data = [
        {
            id: 3,
            name: 'Demandas',
            to: '/demandas',
            notification: counters?.qtd_demandas ? counters.qtd_demandas : 0,
            icon: <IconListDemand color={comum.colors.interfaceGray400}/>,
        },
    ];

    const dataSolicitante = [
        {
            id: 3,
            name: 'Demandas',
            to: '/demandas',
            notification: counters?.qtd_demandas ? counters.qtd_demandas : 0,
            icon: <IconListDemand color={comum.colors.interfaceGray400}/>,
        },
    ];

    const menuItems = user?.profile === 'admin' ? adminData 
        : user?.profile === 'operador' ? operatorData 
        : user?.profile === 'Solicitante' ? dataSolicitante
        : data;

    return (
        <Container>
            {!isOpen && (
                <MenuButton onClick={() => setIsOpen(!isOpen)}>
                    <IconBurguerMenu />
                </MenuButton>
            )}

            <Overlay isOpen={isOpen} onClick={() => setIsOpen(false)} />
            
            <MenuContent isOpen={isOpen} ref={menuRef}>
                <MenuHeader>
                    <LogoContainer>
                        <IconEasyJurLegalOps />
                    </LogoContainer>
                    <MenuButton onClick={() => setIsOpen(false)}>
                        <IconCloseBurguerMenu />
                    </MenuButton>
                </MenuHeader>

                <MenuList>
                    {menuItems.map((item) => (
                        <MenuItem 
                            key={item.id}
                            isActive={location.pathname.includes(item.to)}
                        >
                            <Link to={item.to} onClick={() => setIsOpen(false)}>
                                {item.icon}
                                <span>{item.name}</span>
                                {item.notification > 0 && (
                                    <div className="notification">
                                        {item.notification}
                                    </div>
                                )}
                            </Link>
                        </MenuItem>
                    ))}
                </MenuList>

                <SectionFooter
                    isAdmin={user?.profile !== 'comum'}
                    inProfile={profile}
                >
                    <ProfileFooter
                        isAdmin={user?.profile !== 'comum'}
                        inProfile={profile}
                    >
                        <div className="avatarUser">
                            <IconUser color="#FFF"/>
                        </div>
                        <div 
                            className="sideButtonFooter"
                            ref={openRef}
                            onClick={() => setOpenMenu(!openMenu)}
                        >
                            <IconButtonDots color={comum.colors.interfacePure0}/>
                            {openMenu && <ProfileMenu />}
                        </div>
                    </ProfileFooter>

                    <DescriptionProfileFooter
                        isAdmin={user?.profile !== 'comum'}
                        inProfile={profile}
                    >
                        <h2>{user?.nome.split(' ')[0]}</h2>
                        {user?.profile === 'comum' && <span>D{user?.id}</span>}
                        {user?.profile === 'operador' && <span>P{user?.id}</span>}
                        {user?.profile === 'admin' && <span>J{user?.id}</span>}
                    </DescriptionProfileFooter>
                </SectionFooter>
            </MenuContent>
        </Container>
    );
}